import { createContext, useContext, ReactNode } from 'react';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useAuth } from '@/contexts/AuthContext';

interface AuthorizationContextType {
  roles: string[];
  loading: boolean;
  error: Error | null;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  canManageTemplates: boolean;
  canManageBusiness: boolean;
}

const AuthorizationContext = createContext<AuthorizationContextType | null>(null);

export function AuthorizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const rolesData = useUserRoles(user?.id);

  return (
    <AuthorizationContext.Provider value={rolesData}>
      {children}
    </AuthorizationContext.Provider>
  );
}

const FALLBACK: AuthorizationContextType = {
  roles: [],
  loading: false,
  error: null,
  hasRole: () => false,
  hasAnyRole: () => false,
  isAdmin: false,
  isSuperAdmin: false,
  canManageTemplates: false,
  canManageBusiness: false,
};

let warned = false;

export function useAuthorization() {
  const context = useContext(AuthorizationContext);
  if (!context) {
    if (!warned && typeof console !== 'undefined') {
      warned = true;
      console.warn(
        '[useAuthorization] called outside AuthorizationProvider — returning empty roles. ' +
          'This usually means a top-level component rendered before providers mounted.'
      );
    }
    return FALLBACK;
  }
  return context;
}
